import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ClipboardList, Mic, CheckCircle2, Receipt, Sparkles, FileText,
  ArrowRight, ShieldCheck, AlertTriangle, Stethoscope, Building2,
  Network, Scale, GraduationCap, BookOpen,
} from "lucide-react";
import {
  Reveal, SectionHeader, PrimaryButton, SecondaryButton, Waveform, Marquee, fadeUp,
} from "../components/common";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { INDUSTRIES, PROCESS, WHY, FAQS, CASE_STUDIES, BUSINESS } from "../data/site";

const ICONS = { Stethoscope, Building2, Network, Scale, GraduationCap, BookOpen };

const HERO_LINES = [
  "Documentation you can",
  "use with clarity",
  "and confidence.",
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-brand-bg pt-14 lg:pt-20" data-testid="hero">
      <div className="container-page grid items-center gap-12 pb-16 lg:grid-cols-[1.05fr_0.95fr] lg:pb-24">
        <div>
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="eyebrow block">
            Clinical Documentation • Human Quality Assurance • Business Support
          </motion.span>
          <h1 className="mt-5 font-heading font-800 text-brand-ink text-[40px] leading-[1.14] tracking-tight sm:text-[52px] lg:text-[clamp(58px,4.6vw,68px)]">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden py-[0.1em]">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.55, duration: 0.6 }} className="mt-6 max-w-xl text-[17px] leading-relaxed text-brand-muted md:text-[18px]">
            Primevanta Global provides human-led clinical documentation, medical transcription, editing and quality assurance, scribing, billing and coding support, AI-assisted content review, and professional transcription for healthcare, legal, academic and business clients.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-8 flex flex-wrap gap-3">
            <PrimaryButton to="/contact" testid="hero-primary-cta">Discuss Your Documentation Needs</PrimaryButton>
            <SecondaryButton to="/services" testid="hero-secondary-cta">Explore Our Services</SecondaryButton>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.85, duration: 0.6 }} className="mt-6 text-[13.5px] text-brand-muted">
            New Delhi-based. Remote delivery. India and international enquiries welcome.
          </motion.p>
        </div>

        <motion.div style={{ y }} className="relative">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            whileInView={{ clipPath: "inset(0 0 0% 0)" }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[20px] border border-brand-border shadow-[0_30px_80px_-30px_rgba(11,61,50,0.35)]"
          >
            <img
              src="https://images.unsplash.com/photo-1758691461990-03b49d969495?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Documentation specialist reviewing structured medical records in a naturally lit workspace"
              className="h-[420px] w-full object-cover lg:h-[520px]"
              loading="eager"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-evergreen/60 to-transparent p-5">
              <Waveform bars={44} tone="#FFFFFF" className="opacity-90" />
            </div>
          </motion.div>
          {/* floating quality-check card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 1, duration: 0.6 }}
            className="absolute -left-4 bottom-10 hidden items-center gap-3 rounded-2xl border border-brand-border bg-white p-4 shadow-xl sm:flex"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-alt"><CheckCircle2 className="h-5 w-5 text-brand-primary" /></span>
            <div>
              <p className="font-heading text-[13px] font-700 text-brand-ink">Human quality check</p>
              <p className="text-[12px] text-brand-muted">Final review before delivery</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <TrustStrip />
    </section>
  );
}

function TrustStrip() {
  const items = ["Founded in 2004", "Healthcare documentation foundation", "Human-reviewed workflows", "India & international support", "Medical · legal · academic · business"];
  return (
    <div className="border-t border-brand-border bg-white" data-testid="trust-strip">
      <div className="container-page grid gap-4 py-6 sm:grid-cols-2 lg:grid-cols-5">
        {items.map((t, i) => (
          <Reveal key={t} delay={i} className="flex items-center gap-2.5">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-primary" />
            <span className="text-[13.5px] font-500 text-brand-ink">{t}</span>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

const CORE = [
  { icon: ClipboardList, title: "Clinical Documentation", desc: "Accurate, structured support for clinical records, dictated notes and healthcare documentation workflows.", slug: "clinical-documentation" },
  { icon: Mic, title: "Medical Transcription & Scribing", desc: "Human-led transcription and scribing assistance adapted to specialty, template and workflow requirements.", slug: "medical-transcription" },
  { icon: CheckCircle2, title: "Editing & Quality Assurance", desc: "Detailed review of terminology, context, grammar, formatting, consistency and documentation quality.", slug: "editing-quality-assurance" },
  { icon: Receipt, title: "Medical Billing & Coding Support", desc: "Flexible back-office support for confirmed billing and coding processes, designed around client workflows.", slug: "medical-billing-coding" },
  { icon: Sparkles, title: "AI-Assisted Content Review", desc: "Human editing that improves accuracy, clarity, tone and natural flow in AI-assisted medical and business content.", slug: "ai-content-humanisation" },
  { icon: FileText, title: "Professional Transcription & Data", desc: "Legal, medico-legal, academic and business transcription, along with typing, data entry and document conversion.", slug: "business-transcription" },
];

function Positioning() {
  const stages = [
    { n: "01", t: "Capture", d: "Audio, dictation, source files or AI-assisted drafts" },
    { n: "02", t: "Refine", d: "Transcription, editing, terminology review and formatting" },
    { n: "03", t: "Deliver", d: "Clear, structured and usable professional documentation" },
  ];
  return (
    <section className="bg-brand-alt py-20 lg:py-28" data-testid="positioning">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="The Human Quality Layer" title="More than transcription. A complete documentation support partner." lead="Every document passes through a chain of decisions—capturing information, understanding context, correcting errors, applying structure and preparing the final output. Primevanta Global supports this complete workflow with experienced human oversight." /></Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {stages.map((s, i) => (
            <Reveal key={s.n} delay={i}>
              <div className="group relative h-full rounded-2xl border border-brand-border bg-white p-8 transition-transform duration-200 hover:-translate-y-1">
                <span className="font-heading text-[15px] font-800 text-brand-gold">{s.n}</span>
                <div className="mt-4 mb-3 h-px w-full bg-brand-border" />
                <h3 className="font-heading text-[24px] font-700 text-brand-ink">{s.t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-brand-muted">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10"><SecondaryButton to="/quality-process" testid="positioning-cta">See How We Work</SecondaryButton></div>
      </div>
    </section>
  );
}

function CoreServices() {
  return (
    <section className="py-20 lg:py-28" data-testid="core-services">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="What We Do" title="Specialised support for complex documentation workflows" /></Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CORE.map((c, i) => (
            <Reveal key={c.title} delay={i % 3}>
              <Link to={`/services/${c.slug}`} className="group flex h-full flex-col rounded-2xl border border-brand-border bg-white p-7 transition-transform duration-200 hover:-translate-y-1 hover:border-brand-primary/40" data-testid={`service-card-${c.slug}`}>
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-alt text-brand-primary transition-colors group-hover:bg-brand-primary group-hover:text-white">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-[20px] font-700 text-brand-ink">{c.title}</h3>
                <p className="mt-2.5 flex-1 text-[15px] leading-relaxed text-brand-muted">{c.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-600 text-brand-primary">Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10"><PrimaryButton to="/services" testid="core-view-all">View All Services</PrimaryButton></div>
      </div>
    </section>
  );
}

function HumanAI() {
  const points = ["Context-aware review", "Medical terminology attention", "Tone and meaning preservation", "Formatting consistency", "Escalation of uncertain content", "Final human quality check"];
  return (
    <section className="relative overflow-hidden bg-brand-evergreen py-20 text-white lg:py-28" data-testid="human-ai">
      <div className="grain absolute inset-0" />
      <div className="container-page relative grid gap-14 lg:grid-cols-2">
        <div>
          <span className="eyebrow !text-brand-gold">Human Review & AI</span>
          <h2 className="mt-5 font-heading text-[30px] font-800 leading-[1.12] md:text-[42px]">AI can produce a draft. Human judgement makes it dependable.</h2>
          <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-white/70">
            Automated tools can accelerate documentation, but speed alone does not resolve terminology, context, omissions, formatting or factual inconsistencies. Primevanta Global adds the experienced human review required to turn raw or AI-assisted output into professional documentation.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {points.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-[15px] text-white/85"><CheckCircle2 className="h-4.5 w-4.5 shrink-0 text-brand-gold" style={{ width: 18, height: 18 }} />{p}</li>
            ))}
          </ul>
          <div className="mt-9"><PrimaryButton to="/services/ai-content-humanisation" testid="humanai-cta" className="!bg-brand-gold !text-brand-evergreen hover:!bg-[#c2942f]">Explore AI-Assisted Editing</PrimaryButton></div>
        </div>
        <Reveal className="grid gap-4 self-center">
          <div className="rounded-2xl border border-white/15 bg-white/5 p-6">
            <p className="mb-3 text-[12px] font-700 uppercase tracking-wider text-white/50">Rough AI draft</p>
            <p className="font-mono text-[14px] leading-relaxed text-white/60">pt c/o chest pain x2 days, hx of htn, meds unclear. advised ecg &amp; follow up. no other complaint noted.</p>
          </div>
          <div className="flex justify-center"><span className="rounded-full bg-brand-gold px-4 py-1.5 text-[12px] font-700 uppercase tracking-wider text-brand-evergreen">Human quality layer</span></div>
          <div className="rounded-2xl border border-white/25 bg-white/10 p-6">
            <p className="mb-3 text-[12px] font-700 uppercase tracking-wider text-brand-gold">Refined documentation</p>
            <p className="text-[15px] leading-relaxed text-white">The patient reports chest pain for two days, with a documented history of hypertension. Current medications require confirmation. An ECG and follow-up review were advised. No additional complaints were noted.</p>
          </div>
          <p className="text-center text-[12px] text-white/40">Illustrative example. No real medical information is used.</p>
        </Reveal>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="py-20 lg:py-28" data-testid="industries">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="Industries" title="Documentation expertise across professional sectors" /></Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {INDUSTRIES.map((ind, i) => {
            const Icon = ICONS[ind.icon] || FileText;
            return (
              <Reveal key={ind.title} delay={i % 3}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-brand-border bg-white p-6 transition-transform duration-200 hover:-translate-y-1">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-alt text-brand-slate"><Icon className="h-5.5 w-5.5" style={{ width: 22, height: 22 }} /></span>
                  <div>
                    <h3 className="font-heading text-[17px] font-700 text-brand-ink">{ind.title}</h3>
                    <p className="mt-1 text-[14px] leading-relaxed text-brand-muted">{ind.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-10"><SecondaryButton to="/industries" testid="industries-cta">Find Your Industry</SecondaryButton></div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="bg-brand-alt py-20 lg:py-28" data-testid="why-choose">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="Why Primevanta" title="Experience where accuracy, context and accountability matter" /></Reveal>
        <div className="mt-14 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i % 3}>
              <div className="border-t-2 border-brand-primary/20 pt-5">
                <h3 className="font-heading text-[19px] font-700 text-brand-ink">{w.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-brand-muted">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="py-20 lg:py-28" data-testid="process">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="How We Work" title="A clear process from enquiry to delivery" /></Reveal>
        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i} className="relative">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-brand-primary font-heading text-[15px] font-800 text-brand-primary">{p.n}</span>
                {i < PROCESS.length - 1 && <span className="hidden h-px flex-1 bg-brand-border lg:block" />}
              </div>
              <h3 className="font-heading text-[17px] font-700 text-brand-ink">{p.title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-brand-muted">{p.text}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-10"><PrimaryButton to="/contact" testid="process-cta">Start a Project Discussion</PrimaryButton></div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section className="bg-brand-evergreen py-20 text-white lg:py-28" data-testid="founder">
      <div className="container-page grid items-center gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <Reveal>
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-white/15 bg-white/5">
              <img src={BUSINESS.founderPortrait} alt={`${BUSINESS.founder}, Founder of Primevanta Global`} className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-4 left-6 rounded-xl border border-white/15 bg-brand-evergreen px-5 py-3 shadow-xl">
              <p className="font-heading text-[15px] font-700 text-white">{BUSINESS.founder}</p>
              <p className="text-[12px] text-brand-gold">Founder</p>
            </div>
          </div>
        </Reveal>
        <div>
          <span className="eyebrow !text-brand-gold">Founder-Led</span>
          <h2 className="mt-5 font-heading text-[30px] font-800 leading-[1.15] md:text-[40px]">Founder-led documentation expertise</h2>
          <p className="mt-6 font-serif text-[19px] italic leading-relaxed text-white/85">
            “Primevanta Global was established by {BUSINESS.founder} with a focus on dependable healthcare documentation. Since 2004, the business has expanded from medical transcription into clinical documentation support, quality assurance, business documentation, data services and AI-assisted content review.”
          </p>
          <p className="mt-5 text-[16px] leading-relaxed text-white/70">
            The company’s approach remains grounded in a simple principle: technology may support the process, but professional accountability must remain human.
          </p>
          <div className="mt-8"><PrimaryButton to="/about" testid="founder-cta" className="!bg-brand-gold !text-brand-evergreen hover:!bg-[#c2942f]">Meet {BUSINESS.founder}</PrimaryButton></div>
        </div>
      </div>
    </section>
  );
}

function CaseStudies() {
  return (
    <section className="py-20 lg:py-28" data-testid="case-studies">
      <div className="container-page">
        <Reveal><SectionHeader eyebrow="Case Studies" title="Practical support built around real workflows" /></Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {CASE_STUDIES.map((c, i) => (
            <Reveal key={c.title} delay={i}>
              <div className="flex h-full flex-col rounded-2xl border border-dashed border-brand-border bg-white p-7">
                <span className="mb-4 w-fit rounded-full bg-brand-alt px-3 py-1 text-[12px] font-600 text-brand-slate">{c.tag}</span>
                <h3 className="font-heading text-[19px] font-700 text-brand-ink">{c.title}</h3>
                <p className="mt-3 text-[14px] italic text-brand-muted">Case study awaiting client approval.</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-brand-alt py-20 lg:py-28" data-testid="testimonials">
      <div className="container-page">
        <Reveal className="mx-auto max-w-3xl text-center">
          <SectionHeader align="center" eyebrow="References" title="Client feedback will be published only with permission" lead="Verified client feedback and approved case studies are being prepared. In the meantime, speak directly with Primevanta Global about your workflow, quality expectations and project requirements." />
          <div className="mt-8 flex justify-center"><SecondaryButton to="/contact" testid="testimonial-cta">Request a Professional Reference</SecondaryButton></div>
        </Reveal>
      </div>
    </section>
  );
}

function FAQPreview() {
  return (
    <section className="py-20 lg:py-28" data-testid="faq-preview">
      <div className="container-page grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal><SectionHeader eyebrow="FAQ" title="Answers to common questions" lead="Everything you need to know before starting a documentation project." /><div className="mt-8"><SecondaryButton to="/faqs" testid="faq-all">View All FAQs</SecondaryButton></div></Reveal>
        <Reveal delay={1}>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-brand-border">
                <AccordionTrigger className="text-left font-heading text-[16px] font-600 text-brand-ink hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-brand-muted">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-brand-primary py-20 text-white lg:py-24" data-testid="final-cta">
      <div className="container-page relative">
        <div className="max-w-3xl">
          <h2 className="font-heading text-[32px] font-800 leading-[1.1] md:text-[44px]">Need a dependable human layer in your documentation workflow?</h2>
          <p className="mt-5 text-[17px] leading-relaxed text-white/80">Tell us what you are working with, where quality problems occur and what a successful outcome should look like. We will review the requirement and recommend a practical next step.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryButton to="/contact" testid="final-discuss" className="!bg-white !text-brand-primary hover:!bg-white/90">Discuss Your Project</PrimaryButton>
            <Link to="/contact?type=sample" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-heading font-600 text-[15px] text-white transition-colors hover:bg-white/10" data-testid="final-sample">Request a Sample</Link>
          </div>
          <div className="mt-8 flex items-start gap-2.5 rounded-xl border border-white/20 bg-white/10 p-4 text-[13.5px] text-white/85">
            <AlertTriangle className="mt-0.5 h-4.5 w-4.5 shrink-0 text-brand-gold" style={{ width: 18, height: 18 }} />
            <span>Please do not send confidential patient, legal or commercially sensitive documents through the standard enquiry form.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Positioning />
      <CoreServices />
      <Marquee items={["Clinical Documentation", "Medical Transcription", "Editing & QA", "AI-Assisted Review", "Medical Scribing", "Legal Transcription", "Data Entry"]} />
      <HumanAI />
      <Industries />
      <WhyChoose />
      <Process />
      <Founder />
      <CaseStudies />
      <Testimonials />
      <FAQPreview />
      <FinalCTA />
    </>
  );
}
